import { TimeRange, sharedKeyUtils } from './shared-utils'

// Admin query keys with hierarchical structure
export const adminKeys = {
  // Base key for all admin-related queries
  all: ['admin'] as const,
  
  // Dashboard queries
  dashboard: () => [...adminKeys.all, 'dashboard'] as const,
  dashboardStats: () => [...adminKeys.dashboard(), 'stats'] as const,
  dashboardOverview: () => [...adminKeys.dashboard(), 'overview'] as const,
  dashboardMetrics: (timeRange?: TimeRange) => [
    ...adminKeys.dashboard(), 
    'metrics', 
    { timeRange }
  ] as const,
  
  // User management queries
  users: () => [...adminKeys.all, 'users'] as const,
  allUsers: (page?: number, filters?: UserFilters) => [
    ...adminKeys.users(), 
    'all', 
    { page, filters }
  ] as const,
  user: (id: string) => [...adminKeys.users(), id] as const,
  userActivity: (id: string, timeRange?: TimeRange) => [
    ...adminKeys.users(), 
    id, 
    'activity', 
    { timeRange }
  ] as const,
  userStats: () => [...adminKeys.users(), 'stats'] as const,
  
  // Content management queries
  content: () => [...adminKeys.all, 'content'] as const,
  allImages: (page?: number, status?: string) => [
    ...adminKeys.content(), 
    'images', 
    { page, status }
  ] as const,
  allVideos: (page?: number, status?: string) => [
    ...adminKeys.content(), 
    'videos', 
    { page, status }
  ] as const,
  contentStats: () => [...adminKeys.content(), 'stats'] as const,
  moderationQueue: (type?: 'images' | 'videos') => [
    ...adminKeys.content(), 
    'moderation', 
    { type }
  ] as const,
  
  // System monitoring
  system: () => [...adminKeys.all, 'system'] as const,
  systemHealth: () => [...adminKeys.system(), 'health'] as const,
  systemMetrics: () => [...adminKeys.system(), 'metrics'] as const,
  systemLogs: (level?: LogLevel) => [...adminKeys.system(), 'logs', { level }] as const,
  
  // Notifications
  notifications: () => [...adminKeys.all, 'notifications'] as const,
  headerNotifications: () => [...adminKeys.notifications(), 'header'] as const,
  allNotifications: (page?: number, read?: boolean) => [
    ...adminKeys.notifications(), 
    'all', 
    { page, read }
  ] as const,
  notificationCounts: () => [...adminKeys.notifications(), 'counts'] as const,
  
  // Reports and analytics
  reports: () => [...adminKeys.all, 'reports'] as const,
  usageReport: (timeRange: TimeRange) => [
    ...adminKeys.reports(), 
    'usage', 
    { timeRange }
  ] as const,
  performanceReport: (timeRange: TimeRange) => [
    ...adminKeys.reports(), 
    'performance', 
    { timeRange }
  ] as const,
  contentReport: (timeRange: TimeRange) => [
    ...adminKeys.reports(), 
    'content', 
    { timeRange }
  ] as const,
} as const

// Admin-specific types and interfaces
export interface UserFilters {
  status?: 'active' | 'inactive' | 'suspended'
  role?: 'user' | 'admin'
  sortBy?: 'createdAt' | 'lastActive' | 'name'
  sortOrder?: 'asc' | 'desc'
  search?: string
  dateFrom?: string
  dateTo?: string
  limit?: number
}

export type LogLevel = 'error' | 'warn' | 'info' | 'debug'

export type ContentStatus = 
  | 'pending' 
  | 'approved' 
  | 'rejected' 
  | 'processing' 
  | 'flagged' 
  | 'archived'

// Type for all possible admin query keys
export type AdminQueryKey = typeof adminKeys[keyof typeof adminKeys]

// Utility functions for admin keys
export const adminKeyUtils = {
  // Check if a key is for user management
  isUserManagementKey: (key: readonly unknown[]): boolean => {
    return key.includes('admin') && key.includes('users')
  },
  
  // Check if a key is for content management
  isContentManagementKey: (key: readonly unknown[]): boolean => {
    return key.includes('admin') && key.includes('content')
  },
  
  // Check if a key is for system monitoring
  isSystemKey: (key: readonly unknown[]): boolean => {
    return key.includes('admin') && key.includes('system')
  },
  
  // Check if a key is for notifications
  isNotificationKey: (key: readonly unknown[]): boolean => {
    return key.includes('admin') && key.includes('notifications')
  },
  
  // Extract user ID from admin user key
  getUserIdFromKey: (key: readonly unknown[]): string | null => {
    return sharedKeyUtils.extractIdFromKey(key, 'users', ['all', 'stats'])
  },
  
  // Use shared utilities
  ...sharedKeyUtils,
}

export default adminKeys